/**
 * The referrer bar and the partner card.
 *
 * Every MV Qore call in the theme that is not the site-wide ?ref= capture is in
 * this file, and they are all in the block marked "MV Qore surface" below. The
 * rest is this theme's own UI. That split is deliberate: the SDK is young, the
 * store it will run on first is a demo store, and when a shape turns out to be
 * different from the documentation the change should be in one place.
 *
 * Fails closed everywhere. No SDK, app not installed, tenant not configured, a
 * network error mid-paint: the bar stays hidden. A referral bar that cannot
 * resolve a referrer is worse than no bar.
 */
(() => {
  const bar = document.querySelector('[data-pruvit-rbar]');
  const modal = document.querySelector('[data-pruvit-rmodal]');
  if (!bar || !modal) return;

  const el = {
    ask: bar.querySelector('[data-pruvit-rbar-ask]'),
    avatar: bar.querySelector('[data-pruvit-rbar-avatar]'),
    name: bar.querySelector('[data-pruvit-rbar-name]'),
    title: modal.querySelector('[data-pruvit-rmodal-title]'),
    form: modal.querySelector('[data-pruvit-rform]'),
    input: modal.querySelector('[data-pruvit-rinput]'),
    submit: modal.querySelector('[data-pruvit-rsubmit]'),
    err: modal.querySelector('[data-pruvit-rerr]'),
    cardAvatar: modal.querySelector('[data-pruvit-rcard-avatar]'),
    cardName: modal.querySelector('[data-pruvit-rcard-name]'),
    contact: modal.querySelector('[data-pruvit-rcontact]'),
    none: modal.querySelector('[data-pruvit-rnone]'),
    change: modal.querySelector('[data-pruvit-rchange]'),
  };

  /**
   * Liquid's `t` filter HTML-escapes what it returns, and these strings are
   * set with textContent, which decodes nothing — so an apostrophe reached the
   * screen as "isn&#39;t" on the one string that had one. The copy uses
   * typographic apostrophes now, which `t` leaves alone, but that only fixes
   * the instance: the next string with an ampersand in it would do the same.
   * This closes the class. Assigning to a detached textarea's innerHTML parses
   * entities without running anything, which is the point of using that
   * element rather than a div.
   */
  const decodeEntities = (value) => {
    if (typeof value !== 'string' || !value.includes('&')) return value;
    const box = document.createElement('textarea');
    box.innerHTML = value;
    return box.value;
  };

  let strings = {};
  try {
    const raw = JSON.parse(document.querySelector('[data-pruvit-referrer-strings]').textContent);
    strings = Object.fromEntries(Object.entries(raw).map(([k, v]) => [k, decodeEntities(v)]));
  } catch (e) {
    return; // no copy, no UI
  }

  const ICON = {
    mail: '<path d="M2 4h20v16H2z" /><path d="M2 7l10 6 10-6" />',
    tel: '<path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8.1 9.7a16 16 0 0 0 6 6l1.2-1.2a2 2 0 0 1 2.1-.5c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.7 2z" />',
  };

  const svg = (paths) => {
    const s = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    s.setAttribute('viewBox', '0 0 24 24');
    s.setAttribute('fill', 'none');
    s.setAttribute('stroke', 'currentColor');
    s.setAttribute('stroke-width', '2');
    s.setAttribute('stroke-linecap', 'round');
    s.setAttribute('stroke-linejoin', 'round');
    s.setAttribute('aria-hidden', 'true');
    s.innerHTML = paths;
    return s;
  };

  const initials = (name) =>
    String(name || '')
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => (w[0] ? w[0].toUpperCase() : ''))
      .join('') || '?';

  /**
   * What to call this referrer on screen.
   *
   * A validated referrer can arrive with no name. Measured on the MV Infotrax
   * demo store 2026-09-19: code `rootespinoza` returns isSuccess true with a
   * real userId and tenant, and name, email, phoneNumber and imageUrl all as
   * zero-length strings. So "valid" does not imply "has a name", and the bar
   * read "Recommended by " with an empty span until this existed.
   *
   * The code is the fallback because it is the one thing such a record always
   * has, and naming the referrer by their code is at least true. Three places
   * need this and two of them already guarded it differently, which is the
   * drift this returns to one line.
   */
  /**
   * Write "sentence with an emphasised bit" into an element as text nodes plus
   * one wrapper, so nothing here can inject markup. `template` carries a
   * placeholder; `accent` replaces it inside a <b> or <span>, which is what the
   * bar's call-to-action rule styles.
   */
  const writeWithAccent = (el, template, token, accent, tag) => {
    const parts = String(template || '').split(token);
    el.textContent = '';
    el.append(document.createTextNode(decodeEntities(parts[0] || '')));
    const mark = document.createElement(tag);
    mark.textContent = decodeEntities(accent || '');
    el.append(mark);
    if (parts.length > 1) el.append(document.createTextNode(decodeEntities(parts[1] || '')));
  };

  const displayName = (referrer) =>
    ((referrer && referrer.name) || '').trim() || (referrer && referrer.code) || '';

  /**
   * The photo, or the monogram. Built as DOM rather than assembled as a string:
   * imageUrl is third-party data, and a property assignment cannot be markup.
   * A partner's photo is hosted but not guaranteed, so a 404 falls back to the
   * monogram rather than leaving a broken image where a face should be.
   */
  const paintAvatar = (node, referrer) => {
    const fallback = initials(displayName(referrer));
    node.textContent = '';
    if (!referrer || !referrer.imageUrl) {
      node.textContent = fallback;
      return;
    }
    const img = document.createElement('img');
    img.alt = '';
    img.addEventListener('error', () => {
      node.textContent = fallback;
    });
    img.src = referrer.imageUrl;
    node.appendChild(img);
  };

  const contactRow = (iconPaths, value, actions) => {
    const row = document.createElement('div');
    row.className = 'pruvit-rc';
    row.appendChild(svg(iconPaths));

    const v = document.createElement('span');
    v.className = 'pruvit-rc__v';
    v.textContent = value; // third-party data: text, never markup
    row.appendChild(v);

    const acts = document.createElement('span');
    acts.className = 'pruvit-rc__acts';
    actions.forEach(({ href, label }) => {
      const a = document.createElement('a');
      a.className = 'pruvit-rc__act';
      a.href = href;
      a.textContent = label;
      acts.appendChild(a);
    });
    row.appendChild(acts);
    return row;
  };

  // ---------------------------------------------------------------------------
  // MV Qore surface — every SDK call the bar makes is in here.
  // ---------------------------------------------------------------------------
  const mvq = {
    sdk: null,
    settings: null,

    /** Resolves false when there is no usable MV Qore on this page. */
    async ready() {
      this.sdk = await window.pruvitMVQore;
      if (!this.sdk) return false;
      const status = await this.sdk.getStatus();
      if (!status || !status.installed || !status.configured) {
        // error present means "could not reach the app", absent means "really
        // not installed" — worth telling apart in a console, not in the UI.
        if (status && status.error) {
          console.warn('[pruvit] MV Qore status check failed:', status.error);
        }
        return false;
      }
      // getSettings() used to be fetched here, and only ever to feed the two
      // copy helpers below. Both strings are the theme's now, so this is one
      // fewer serial round trip before the bar can paint — 784ms on staging.
      return true;
    },

    active() {
      return this.sdk.getActiveReferrer(); // never rejects; null when none
    },

    validate(code) {
      return this.sdk.validateReferrer(code);
    },

    /** The whole result, never result.referrer — the flattened view throws. */
    persist(result) {
      this.sdk.persistReferrer(result);
    },

    clear() {
      this.sdk.clearReferrer();
    },



    /**
     * Contact details for the partner card.
     *
     * The SDK documents `referrer` as { code, userId, name, imageUrl } only,
     * and MV Qore confirmed (2026-09-19) that referrer validation returns the
     * customer's contact info — which puts it somewhere in `record`, the full
     * server response, whose shape is not documented. The prototype's mock of
     * this endpoint returned it at record.node.{email,phoneNumber}.
     *
     * So: look in the plausible places, and say so loudly when none of them
     * has it, because the first run on a store with the SDK deployed is what
     * settles the real shape. Do not "fix" this by guessing — read the warning.
     */
    contactOf(result) {
      if (!result) return { email: null, phone: null };
      const record = result.record || {};
      const places = [record.node, record, result.referrer, result];
      let email = null;
      let phone = null;
      for (const p of places) {
        if (!p || typeof p !== 'object') continue;
        email = email || p.email || p.emailAddress || null;
        phone = phone || p.phoneNumber || p.phone || null;
      }
      if (!email && !phone) {
        console.warn(
          '[pruvit] no referrer contact details found. Keys seen — ' +
            'record: [' + Object.keys(record).join(', ') + '], ' +
            'record.node: [' + Object.keys(record.node || {}).join(', ') + ']'
        );
      }
      return { email, phone };
    },
  };
  // ---------------------------------------------------------------------------

  /** The last validated result, kept whole so the card can read `record`. */
  let current = null;

  /**
   * One referrer, two places that draw it.
   *
   * The bar and the lead forms each resolve the referrer once when they start
   * and then paint from their own copy. Nothing told the other when it
   * changed, so entering a code on the Create Account form validated, showed
   * "Referred by …" in the form, and left the bar still asking who referred
   * you until the page was reloaded. QA found it on the account form; it was
   * true in both directions.
   *
   * Everything that changes the referrer says so on this event and everything
   * that draws one listens. Listeners never dispatch, so there is no loop, and
   * repainting from your own announcement is idempotent.
   */
  const REFERRER_EVENT = 'pruvit:referrer';
  const announce = (result) =>
    document.dispatchEvent(new CustomEvent(REFERRER_EVENT, { detail: { result: result || null } }));

  const paintBar = (result) => {
    current = result || null;
    const referrer = result && result.referrer;
    if (referrer && referrer.code) {
      const shown = displayName(referrer);
      paintAvatar(el.avatar, referrer);
      // Built as nodes rather than a markup string. The name is server data
      // and this used to be innerHTML of something formatToolbarTitle had
      // escaped for us; owning the string means owning that, and a text node
      // cannot be talked into being markup.
      writeWithAccent(el.name, strings.shoppingWith, '%NAME%', shown, 'span');
      el.name.setAttribute('aria-label', String(strings.detailsFor || '').replace('%NAME%', shown));
      bar.dataset.state = 'has';
      // Worth a line in the console rather than a silent oddity on screen: a
      // record with no name is a server-side state, not a bug here.
      if (!((referrer.name || '').trim())) {
        console.warn(
          '[pruvit] referrer ' + referrer.code + ' validated with no name; showing the code.'
        );
      }
    } else {
      // The action phrase is a <b> so the shared call-to-action rule can find
      // it. Their prompt was one flat sentence, which is why the prompt state
      // never went periwinkle.
      writeWithAccent(el.ask, strings.prompt + ' %CTA%', '%CTA%', strings.promptCta, 'b');
      bar.dataset.state = 'none';
    }
    // The bar is already on screen holding its space; this is the swap out of
    // the loading state, not a reveal. hidden is cleared anyway so the paint
    // still works if something hid the bar first.
    bar.hidden = false;
    bar.removeAttribute('aria-busy');
  };

  const paintCard = (result) => {
    const referrer = (result && result.referrer) || {};
    paintAvatar(el.cardAvatar, referrer);
    el.cardName.textContent = displayName(referrer);

    const { email, phone } = mvq.contactOf(result);
    el.contact.textContent = '';

    if (email) {
      el.contact.appendChild(
        contactRow(ICON.mail, email, [{ href: `mailto:${email}`, label: strings.email }])
      );
    }
    if (phone) {
      const digits = String(phone).replace(/[^\d+]/g, '');
      el.contact.appendChild(
        contactRow(ICON.tel, phone, [
          { href: `tel:${digits}`, label: strings.call },
          { href: `sms:${digits}`, label: strings.text },
        ])
      );
    }
    el.none.hidden = Boolean(email || phone);
  };

  const open = (mode) => {
    modal.dataset.mode = mode;
    // The dialog's accessible name is this heading, so it has to stay — but
    // "Who referred you?" is a question already answered in card mode.
    el.title.textContent = mode === 'card' ? strings.titleCard : strings.titleEntry;
    modal.showModal();
    document.body.style.overflow = 'hidden';
    if (mode === 'entry') {
      el.err.textContent = '';
      // Synchronously, inside the gesture's call stack: iOS only raises the
      // keyboard when focus() runs there, and a setTimeout breaks that.
      el.input.focus({ preventScroll: true });
    }
  };

  const close = () => {
    if (modal.open) modal.close();
  };

  // <dialog> restores nothing on close, and Escape closes it without passing
  // through our handler, so the scroll lock is released on the close event.
  modal.addEventListener('close', () => {
    document.body.style.overflow = '';
  });

  modal.querySelector('[data-pruvit-rmodal-close]').addEventListener('click', close);

  // The panel is a child of the dialog, so a click landing on the dialog
  // itself is a click on the backdrop area.
  modal.addEventListener('click', (e) => {
    if (e.target === modal) close();
  });

  el.ask.addEventListener('click', () => open('entry'));

  el.name.addEventListener('click', () => {
    if (!current) return;
    paintCard(current);
    open('card');
  });

  el.change.addEventListener('click', () => {
    mvq.clear();
    paintBar(null);
    announce(null);
    el.input.value = '';
    open('entry');
  });

  const messageFor = (error) =>
    ({
      INVALID_REFERRER_CODE: strings.errInvalid,
      REFERRER_DATA_NOT_FOUND: strings.errInvalid,
      RATE_LIMITED: strings.errRate,
    })[error && error.code] || strings.errGeneric;

  el.form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const code = el.input.value.trim();
    if (!code) {
      el.err.textContent = strings.errEmpty;
      return;
    }

    el.err.textContent = '';
    // An ellipsis in place of the label was this button's whole loading state:
    // it changed the width, it could not move, and it read as the label having
    // gone missing. .pruvit-busy keeps the label's box and spins a ring in it.
    el.submit.classList.add('pruvit-busy');
    el.submit.setAttribute('aria-busy', 'true');
    el.submit.disabled = true;

    try {
      const result = await mvq.validate(code);
      mvq.persist(result);
      paintBar(result);
      announce(result);
      close();
    } catch (error) {
      el.err.textContent = messageFor(error);
    } finally {
      el.submit.disabled = false;
      el.submit.removeAttribute('aria-busy');
    }
  });

  (async () => {
    // The bar ships visible in its loading state, so the failure paths have to
    // take the space back rather than simply declining to reveal it. Both of
    // them collapse it: a referral bar that cannot resolve a referrer is worse
    // than no bar, and an empty navy strip is worse than either.
    try {
      if (!(await mvq.ready())) {
        bar.hidden = true;
        return;
      }
      paintBar(await mvq.active());
      // Changes made by a lead form, after this has already painted.
      document.addEventListener(REFERRER_EVENT, (event) => paintBar(event.detail && event.detail.result));
    } catch (error) {
      bar.hidden = true;
      console.warn('[pruvit] referrer bar could not paint:', error);
    }
  })();

  // ---- how much of the screen the keyboard is holding -----------------------
  //
  // Craig, 2026-09-24: the referrer sheet was opening underneath the keyboard.
  //
  // `position: fixed` resolves against the LAYOUT viewport, and iOS does not
  // shrink that for the keyboard — it leaves it full height and covers the
  // bottom. window.visualViewport is the part actually on screen, so the
  // difference between the two is what the keyboard (plus any browser UI) has
  // taken. Published as two custom properties on the root so CSS can anchor to
  // the visible area instead of the layout one:
  //
  //   --pruvit-kb    how much of the bottom is covered. 0 with no keyboard.
  //   --pruvit-vvh   the height that is actually visible.
  //
  // Set here because the referrer sheet is the only panel in this theme that
  // opens a keyboard on itself — showModal() focuses the code field, which is
  // the point of the panel. Written to :root rather than to the panel so any
  // other sheet that grows a text field can use them without moving this.
  //
  // offsetTop matters as well as height: with the keyboard up, iOS scrolls the
  // visual viewport within the layout one, and a sheet that ignored the offset
  // would drift as the page moved under it.
  (function () {
    var vv = window.visualViewport;
    if (!vv) return;

    var root = document.documentElement;
    var frame = null;

    var apply = function () {
      frame = null;
      var covered = Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop));
      root.style.setProperty('--pruvit-kb', covered + 'px');
      root.style.setProperty('--pruvit-vvh', Math.round(vv.height) + 'px');
    };

    // Throttled: the scroll event fires continuously while the visual viewport
    // moves, and this writes to the root, which invalidates style on everything
    // reading those properties.
    var schedule = function () {
      if (frame) return;
      frame = requestAnimationFrame(apply);
    };

    apply();
    vv.addEventListener('resize', schedule);
    vv.addEventListener('scroll', schedule);
  })();
})();
